import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { RefreshCw, Database, Key, Link, Zap } from 'lucide-react';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';

/**
 * SchemaViewerPage - Development page to view database schema (LIVE from database)
 * TODO: Delete this file before production deployment
 */
const SchemaViewerPage = () => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});

  const { notify } = useNotifications();

  const loadSchema = useCallback(async () => {
    try {
      setLoading(true);
      // error cleared
      const response = await devFilesApi.getLiveSchema();
      setSchema(response.data);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load schema');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSchema();
  }, [loadSchema]);

  // Flatten schema into rows for the data grid
  const flattenedData = useMemo(() => {
    if (!schema?.tables) return [];

    const rows = [];
    schema.tables.forEach(table => {
      table.columns.forEach(col => {
        rows.push({
          id: `${table.name}.${col.name}`,
          table: table.name,
          column: col.name,
          dataType: col.dataType,
          nullable: col.isNullable ? 'NULL' : 'NOT NULL',
          isPrimaryKey: col.isPrimaryKey,
          isIdentity: col.isIdentity,
          foreignKey: col.foreignKeyTable || ''
        });
      });
    });
    return rows;
  }, [schema]);

  // Apply filters
  const filteredData = useMemo(() => {
    return flattenedData.filter(row => {
      return Object.entries(filters).every(([key, values]) => {
        if (!values || values.length === 0) return true;
        return values.includes(String(row[key] || ''));
      });
    });
  }, [flattenedData, filters]);

  // Column definitions for the data grid
  const columns = [
    { key: 'table', label: 'Table' },
    { key: 'column', label: 'Column' },
    { key: 'dataType', label: 'Data Type' },
    { key: 'nullable', label: 'Nullable' },
    { key: 'isPrimaryKey', label: 'PK' },
    { key: 'isIdentity', label: 'Identity' },
    { key: 'foreignKey', label: 'Foreign Key' }
  ];

  // Stats
  const tableCount = schema?.tables?.length || 0;
  const columnCount = flattenedData.length;
  const pkCount = flattenedData.filter(r => r.isPrimaryKey).length;
  const fkCount = flattenedData.filter(r => r.foreignKey).length;

  if (loading) {
    return <div className="dev-page loading">Loading schema...</div>;
  }

  return (
    <div className="dev-module schema-viewer-page">
      {/* Header - uses unified dev-module styling */}
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Database size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Database Schema
            <span className="live-badge">
              <Zap size={12} /> LIVE
            </span>
          </h1>
          <span className="dev-module-meta">{schema?.databaseName}</span>
        </div>
        <div className="dev-module-header-right">
          {/* CRUD buttons */}
          <button
            className="crud-btn"
            onClick={loadSchema}
            disabled={loading}
            title="Refresh Schema"
          >
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      <div className="schema-stats-bar">
        <span className="stat-item">
          <Database size={14} />
          {tableCount} tables
        </span>
        <span className="stat-item">
          {columnCount} columns
        </span>
        <span className="stat-item">
          <Key size={14} />
          {pkCount} primary keys
        </span>
        <span className="stat-item">
          <Link size={14} />
          {fkCount} foreign keys
        </span>
        <span className="stat-item muted">
          Queried: {schema?.lastQueried ? new Date(schema.lastQueried).toLocaleString() : 'Unknown'}
        </span>
      </div>

      {/* Content area */}
      <div className="dev-module-content-full">
        {/* Active filters display */}
        {Object.entries(filters).some(([, v]) => v?.length > 0) && (
          <div className="schema-active-filters">
            <span className="filter-label">Active filters:</span>
            {Object.entries(filters).map(([key, values]) =>
              values?.length > 0 && (
                <span key={key} className="filter-tag">
                  {key}: {values.length === 1 ? values[0] : `${values.length} selected`}
                  <button
                    className="filter-tag-remove"
                    onClick={() => setFilters(prev => ({ ...prev, [key]: [] }))}
                  >
                    ×
                  </button>
                </span>
              )
            )}
            <button
              className="clear-all-filters"
              onClick={() => setFilters({})}
            >
              Clear all
            </button>
          </div>
        )}

        <div className="schema-results-count">
          Showing {filteredData.length} of {flattenedData.length} columns
        </div>

        <div className="schema-grid-container">
          <FilterableDataGrid
            data={filteredData}
            columns={columns}
            filters={filters}
            setFilters={setFilters}
          />
        </div>
      </div>
    </div>
  );
};

export default SchemaViewerPage;
